import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { ArrowLeft, ExternalLink, MessageSquare, Play, Volume2, FileText, Calendar, Clock, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";

const kindIcon = { video: Play, audio: Volume2, pdf: FileText };
const badge = (s) => s === "completed" ? "Terminée" : s === "in_progress" ? "En cours" : "À faire";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "";

export default function LearnerSessionDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [comments, setComments] = useState([]);
  const [missions, setMissions] = useState([]);
  const [resources, setResources] = useState([]);

  const load = async () => {
    const [ss, cc, mm, rr] = await Promise.all([
      api.get(`/learner/sessions/${id}`),
      api.get(`/learner/sessions/${id}/comments`),
      api.get(`/learner/missions`),
      api.get(`/learner/resources`),
    ]);
    setS(ss.data); setComments(cc.data);
    setMissions(mm.data.filter(m => m.session_id === id));
    setResources(rr.data.filter(r => r.session_id === id));
  };
  useEffect(() => { load(); }, [id]);

  const toggleMission = async (m) => {
    await api.patch(`/learner/missions/${m.id}/complete`, { completed: !m.completed });
    load();
  };

  if (!s) return <div className="text-[#5C6561]">Chargement…</div>;

  const learnerToken = localStorage.getItem("dec_learner_token");

  return (
    <div className="lg:ml-56">
      <Link to="/learner/seances" className="text-sm text-[#5C6561] inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Retour</Link>
      <div className="text-xs uppercase tracking-wider text-[#D95D39] font-semibold">Séance {s.number}</div>
      <h1 className="font-serif-accent text-3xl mt-1">{s.title}</h1>
      <div className="mt-2 text-sm text-[#5C6561]">Statut : {badge(s.status)}</div>

      {s.description && (
        <div className="dec-card p-5 mt-5">
          <h3 className="font-serif-accent text-lg mb-1">Description</h3>
          <p className="text-sm whitespace-pre-wrap">{s.description}</p>
        </div>
      )}
      {s.objectives && (
        <div className="dec-card p-5 mt-4">
          <h3 className="font-serif-accent text-lg mb-1">Objectifs</h3>
          <p className="text-sm whitespace-pre-wrap">{s.objectives}</p>
        </div>
      )}

      {comments.length > 0 && (
        <div className="dec-card p-5 mt-4 dec-side-accent">
          <div className="flex items-center gap-2 mb-3"><MessageSquare className="w-4 h-4 text-[#E5983B]" /><h3 className="font-serif-accent text-lg">Commentaires de la coach</h3></div>
          <div className="space-y-3">
            {comments.map(c => (
              <div key={c.id} className="text-sm text-[#1F2421]">
                {c.text}
                <div className="text-[11px] text-[#5C6561] mt-1">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {missions.length > 0 && (
        <div className="dec-card p-5 mt-4">
          <h3 className="font-serif-accent text-lg mb-3">Missions de cette séance</h3>
          <div className="space-y-2">
            {missions.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F4EFEA]">
                <input type="checkbox" checked={m.completed} onChange={() => toggleMission(m)} className="w-5 h-5 accent-[#D95D39]" data-testid={`learner-mission-toggle-${m.id}`} />
                <div className="flex-1"><div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-sm">{m.title}</span>
                {!m.completed && m.publish_date && ((Date.now() - new Date(m.publish_date).getTime())/86400000) <= 7 && ((Date.now() - new Date(m.publish_date).getTime())/86400000) >= -1 && <span className="dec-pill text-[10px] px-2 py-0.5 bg-[#D95D39] text-white">Nouvelle</span>}</div>
                <div className="flex flex-wrap gap-3 mt-0.5 text-[11px] text-[#5C6561]">
                  {m.publish_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Publiée le {fmtDate(m.publish_date)}</span>}
                  {m.deadline && <span className="inline-flex items-center gap-1 text-[#C04E2D]"><Clock className="w-3 h-3" /> À rendre le {fmtDate(m.deadline)}</span>}
                </div>
                {(m.attachment_url || m.attachment_path) && (
                  <div className="mt-1 flex flex-wrap gap-3">
                    {m.attachment_url && (
                      <button onClick={() => window.open(m.attachment_url, "_blank", "noopener,noreferrer")} className="text-xs text-[#D95D39] font-medium inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Lien
                      </button>
                    )}
                    {m.attachment_path && (
                      <button onClick={async () => { const { openStorageFile } = await import("@/lib/openStorageFile"); try { await openStorageFile(m.attachment_path); } catch {} }} className="text-xs text-[#D95D39] font-medium inline-flex items-center gap-1">
                        <Paperclip className="w-3 h-3" /> {m.attachment_name || "Fichier"}
                      </button>
                    )}
                  </div>
                )}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {resources.length > 0 && (
        <div className="dec-card p-5 mt-4">
          <h3 className="font-serif-accent text-lg mb-3">Ressources</h3>
          <div className="space-y-2">
            {resources.map(r => {
              const Icon = kindIcon[r.kind] || FileText;
              const openIt = async () => {
                if (r.url) window.open(r.url, "_blank", "noopener,noreferrer");
                else if (r.file_path) {
                  const { openStorageFile } = await import("@/lib/openStorageFile");
                  try { await openStorageFile(r.file_path); } catch {}
                }
              };
              return (
                <button key={r.id} onClick={openIt}
                   className="w-full text-left flex items-center gap-3 p-3 rounded-lg bg-[#F4EFEA] hover:bg-[#EBE3D8]">
                  <Icon className="w-4 h-4 text-[#D95D39]" />
                  <div className="flex-1"><div className="font-semibold text-sm">{r.title}</div></div>
                  <ExternalLink className="w-4 h-4 text-[#5C6561]" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
